import type { NotificationRow } from "@/api/notifications";
import { payloadString } from "@/lib/notificationRoute";

/**
 * MV2-1240 — which alarm notifications interrupt the user, pure. The bell
 * poll (30 s) brings the rows; this decides what is new, what is urgent and
 * what the toast says. Critical and major interrupt; minor and info wait in
 * the bell. A notice about an alarm that has already cleared never toasts.
 */

const RANK: Record<string, number> = { critical: 4, high: 3, major: 3, medium: 2, minor: 2, low: 1, info: 1 };

export type NoticeLevel = "critical" | "major";

/** The alarm's severity as the producer stamped it. */
export function severityOf(row: Pick<NotificationRow, "payload">): string | null {
  return payloadString(row.payload, "severity")?.toLowerCase() ?? null;
}

/** When the alarm behind the notice cleared, if it has. */
export function clearedAt(row: Pick<NotificationRow, "payload">): string | null {
  return payloadString(row.payload, "resolved_at");
}

/** critical / major for an alarm that should interrupt; null otherwise. */
export function urgency(row: Pick<NotificationRow, "category" | "payload">): NoticeLevel | null {
  if (row.category !== "alarm" || clearedAt(row)) return null;
  const rank = RANK[severityOf(row) ?? ""] ?? 0;
  return rank >= 4 ? "critical" : rank >= 3 ? "major" : null;
}

/** One key per alarm, so two notices about one alarm (watcher + scope) toast once. */
export const alarmKey = (row: Pick<NotificationRow, "id" | "related_id">): string => `alarm:${row.related_id ?? row.id}`;

/**
 * The urgent notices in ``rows`` not seen before, oldest first, one per
 * alarm. ``seen`` holds notification ids already on screen or in the bell
 * when the page opened.
 */
export function freshUrgent(rows: NotificationRow[], seen: ReadonlySet<string>): NotificationRow[] {
  const out = new Map<string, NotificationRow>();
  for (const r of [...rows].sort((a, b) => a.created_at.localeCompare(b.created_at))) {
    if (seen.has(r.id) || r.read_at || r.dismissed_at || !urgency(r)) continue;
    if (!out.has(alarmKey(r))) out.set(alarmKey(r), r);
  }
  return [...out.values()];
}

/** The toast's two lines: "Major · Cork Airport", then the alarm. */
export function noticeText(row: NotificationRow): { title: string; description: string } {
  const level = urgency(row) === "critical" ? "Critical" : "Major";
  const name = payloadString(row.payload, "alarm_name") ?? row.title;
  return {
    title: row.site_name ? `${level} · ${row.site_name}` : level,
    description: [name, row.snippet].filter(Boolean).join(" · "),
  };
}

/** How long a toast stays: a critical one until dismissed, a major one 10 s. */
export const noticeDuration = (level: NoticeLevel): number => (level === "critical" ? Infinity : 10_000);

/** More than this many at once collapse into one "N new alarms" toast. */
export const MAX_TOASTS = 3;
